import React, { useCallback, useState } from 'react'
import { BaseStepInputProps, OnboardingMode, UseStepFunc, defaultStepFormProps } from '../types.d'
import { Route, Switch, useRouteMatch } from 'react-router-dom'
import * as routing from '../../constants/routes'
import { OnboardingContactInformationSchema, OnboardingStepKey } from '@cbgms/api'
import ContactInformationForm from './ContactInformationForm'
import { ValidatedWholesalerCredentials } from '../OnboardingWholesalerCredentialsStep/types.d'
import { useDispatch } from 'react-redux'
import { useCompleteOnSubmit } from '../use-complete-on-submit'
import { useTranslation } from 'react-i18next'
import { Box } from '@mui/material'
import { useShouldExecuteStep } from '../use-should-execute-step'
import { OnboardingState } from 'app/auth/components/hooks/use-onboarding-setup'
import { updateShop } from 'app/shops/state/actions'
import { updateValidateShopDetailsStepRequest } from '@cbgms/api/modules/shops/requests'
import { useDefaultSkipSetting } from '../use-default-skip-setting'
import { ContactInformationCTA } from './ContactInformationCTA'
import { useShouldRunInBackground } from '../use-should-run-in-background'
import { useStepData } from '../use-step-data'
import GenericDuplicationError from 'app/auth/components/onboarding/steps/shared/GenericDuplicationError'
import InviteUserToOtherShop from 'app/auth/components/onboarding/steps/shared/InviteUserToOtherShop'

interface StepProps {
  basePath: string
  validatedVATNumber: string | null
  wholesalerCredentials: ValidatedWholesalerCredentials | null
  onSubmit: (data: OnboardingContactInformationSchema) => void
  onboardingInfo: OnboardingState
}

interface StepInputProp {
  themeUUID?: string
  validatedVATNumber: string | null
  wholesalerCredentials: ValidatedWholesalerCredentials | null
}
interface ContactInformationProps extends BaseStepInputProps, StepInputProp {}

interface StepOutput {
  shopContactInformation: OnboardingContactInformationSchema | null
}

const useIsOnRoute = (basePath: string): boolean => {
  const match = useRouteMatch({ path: routing.pathVerifyContactInformation(basePath), exact: true })
  return !!match
}

export const useContactInformationStep: UseStepFunc<StepProps, StepInputProp, StepOutput> = ({
  basePath,
  validatedVATNumber,
  wholesalerCredentials,
  themeUUID,
  onboardingInfo
}: ContactInformationProps) => {
  const [shopContactInformation, setShopContactInformation] = useState<OnboardingContactInformationSchema | null>(null)
  const dispatch = useDispatch()
  const stepName = OnboardingStepKey.ShopDetails
  const stepData = useStepData(stepName, onboardingInfo)
  const shouldExecuteStep = useShouldExecuteStep(stepName, onboardingInfo)
  const shouldRunInBackground = useShouldRunInBackground(stepName, onboardingInfo)
  const hideSkip = useDefaultSkipSetting(onboardingInfo)
  const showStepperHeader = useIsOnRoute(basePath)

  const onStepSubmit = useCallback(
    async (data: OnboardingContactInformationSchema) => {
      if (data) {
        setShopContactInformation({ ...data, ThemeUUID: themeUUID })
      }
    },
    [setShopContactInformation, themeUUID]
  )
  const onPersist = useCallback(async () => {
    // we only want to store things for the existing shops.
    // the onboarding where no shop exists will create their shop in the persist of the 'SignUpCredentialsStep'
    await dispatch(updateValidateShopDetailsStepRequest(false))
    if (onboardingInfo.onboardingMode === OnboardingMode.OnboardingExistingShop) {
      await dispatch(updateShop(onboardingInfo.shop!.Data.ID, shopContactInformation as any))
    }
  }, [onboardingInfo.onboardingMode, dispatch, shopContactInformation])
  const complete = useCompleteOnSubmit(onStepSubmit)

  return {
    Component: ContactInformationStep,
    CTAComponent: ContactInformationCTA,
    StepperText,
    isCompleted: complete.isCompleted,
    showInStepper: true,
    showStepperHeader,
    step: stepName,
    stepData,
    shouldExecuteStep,
    shouldRunInBackground,
    hideSkip,
    stepProps: {
      basePath,
      validatedVATNumber,
      wholesalerCredentials,
      onSubmit: complete.onSubmit,
      duplicationCheck: stepData.DuplicationCheck || [],
      onboardingInfo
    },
    persister: {
      onPersist,
      persistOrder: 3
    },
    shopContactInformation
  }
}

const StepperText: React.FC<{}> = () => {
  const { t } = useTranslation()
  return <>{t('onboarding.step_title_contact_information')}</>
}

export const ContactInformationStep: React.FC<defaultStepFormProps & StepProps> = ({ basePath, ...rest }) => {
  return (
    <Switch>
      <Route path={routing.pathDuplicationError(routing.pathVerifyContactInformation(basePath))} component={GenericDuplicationError} />
      <Route path={routing.pathInviteUserToOtherShop(routing.pathVerifyContactInformation(basePath))} component={InviteUserToOtherShop} />
      <Route
        path={routing.pathVerifyContactInformation(basePath)}
        render={() => (
          <Box textAlign='left'>
            <ContactInformationForm basePath={basePath} {...rest} />
          </Box>
        )}
      />
    </Switch>
  )
}
